import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  ChatCircleDots,
  FileText,
  Target,
} from "@phosphor-icons/react";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import SeoPageShell from "@/components/SeoPageShell";

const reviewQuestions = [
  {
    icon: ChatCircleDots,
    title: "What actually happened?",
    copy: "Start with the moments that changed the commercial direction of the call, not a compressed retelling of every topic discussed.",
  },
  {
    icon: FileText,
    title: "What did it really mean?",
    copy: "Separate what the customer said from what it implies about urgency, decision process, objections, risk, and buying intent.",
  },
  {
    icon: WhistleIcon,
    title: "What could you have done better?",
    copy: "Tie coaching to specific moments in the conversation so the lesson is concrete enough to reuse on the next similar call.",
  },
  {
    icon: Target,
    title: "What should happen next?",
    copy: "Carry the useful context forward into the next conversation instead of letting the review end as a document nobody reopens.",
  },
];

const softwareChecklist = [
  "Show the evidence behind an interpretation, not just the interpretation itself.",
  "Explain why a moment mattered to the outcome of the conversation.",
  "Turn critique into practical coaching that can be reused in a future call.",
  "Preserve objections, commitments, stakeholders, and changes across conversations.",
  "Prepare the next call from the actual relationship context, not from a generic playbook.",
];

function trackCta(section, ctaId) {
  trackPublicEvent("cta_click", {
    source: "sales_call_review",
    section,
    cta_id: ctaId,
  });
}

export default function SalesCallReviewPage() {
  useEffect(() => {
    const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    scrollToTop();
    const frame = window.requestAnimationFrame(scrollToTop);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <SeoPageShell source="sales_call_review">
      <main>
        <section className="seo-marketing-hero product-feature-hero sales-call-review-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-cyan-700/10 blur-[135px]" />
          </div>
      
          <div className="relative mx-auto max-w-7xl">
      
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              Review the call to understand
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">what actually changed the outcome.</span>
            </h1>
      
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              A useful sales call review should do more than summarize the transcript or score talk time. It should show the evidence, explain what it meant, turn that into practical coaching, and carry the important context into the next conversation.
            </p>
      
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("hero", "sign_up_free")}
                className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("hero", "see_demo")}
                className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">A better review framework</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Four questions worth answering after a sales call.
              </h2>
              <p className="marketing-lead-copy mt-6">
                The goal is not to produce more notes. It is to leave the review with a clearer model of the deal and a better next move.
              </p>
            </div>
      
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {reviewQuestions.map(({ icon: Icon, title, copy }, index) => (
                <article key={title} className="sales-call-review-card">
                  <div className="sales-call-review-card__topline">
                    <span className="sales-call-review-card__number">0{index + 1}</span>
                    <Icon size={21} weight="duotone" />
                  </div>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">Evidence before opinion</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Start with the moment. Then explain why it mattered.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Generic coaching is easy to ignore. A review becomes useful when the conclusion can be traced back to what the customer or rep actually said.
              </p>
            </div>
      
            <div className="sales-call-review-example">
              <div className="sales-call-review-example__header">
                <span>Example review</span>
                <span>Discovery call</span>
              </div>
      
              <div className="sales-call-review-example__quote">
                <span className="sales-call-review-speaker">CLIENT</span>
                <ChatCircleDots size={22} weight="duotone" aria-hidden="true" />
                <blockquote>
                  “I can probably get my manager on board. Procurement is the part I’m less sure about.”
                </blockquote>
              </div>
      
              <div className="sales-call-review-example__analysis">
                <div>
                  <span>EVIDENCE</span>
                  <p>The champion separates personal support from procurement confidence.</p>
                </div>
                <div>
                  <span>INTERPRETATION</span>
                  <p>The deal is not blocked by interest. The unresolved risk is the internal buying process and who will carry the case into procurement.</p>
                </div>
                <div>
                  <span>COACHING</span>
                  <p>Do not leave the internal sale entirely with the champion. Map procurement now and help them build the case before the next step.</p>
                </div>
              </div>
      
              <div className="sales-call-review-example__next">
                <span>NEXT CONVERSATION</span>
                <p>Ask who procurement needs involved, what evidence they require, and what would make the internal approval easier to defend.</p>
              </div>
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <span className="sales-call-review-kicker">Beyond a single call</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The review should compound across conversations.
              </h2>
              <p className="marketing-lead-copy mt-6">
                One call can reveal an objection, a promise, a stakeholder, or a change in buying intent. The useful part is remembering what happened to those signals later.
              </p>
              <p className="marketing-lead-copy mt-5">
                That is where a call review becomes relationship intelligence: not just what happened today, but what changed since the last conversation and what that should change about the next one.
              </p>
            </div>
      
            <div className="sales-call-review-memory">
              <div className="sales-call-review-memory__row">
                <span>OBJECTION</span>
                <strong>Procurement uncertainty</strong>
                <small>Raised → clarified → resolved</small>
              </div>
              <div className="sales-call-review-memory__row">
                <span>STAKEHOLDER</span>
                <strong>VP Operations</strong>
                <small>Not involved → requested for validation</small>
              </div>
              <div className="sales-call-review-memory__row">
                <span>COMMITMENT</span>
                <strong>Internal business case</strong>
                <small>Promised before next call</small>
              </div>
              <div className="sales-call-review-memory__row is-next">
                <span>NEXT STEP</span>
                <strong>Validate the approval path</strong>
                <small>Prepare around what changed, not from zero</small>
              </div>
            </div>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">What to look for</span>
            <h2 className="mt-4 max-w-4xl text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Good sales call review software should make the next conversation better.
            </h2>
            <div className="mt-10 grid gap-4">
              {softwareChecklist.map((item) => (
                <div key={item} className="sales-call-review-check">
                  <CheckCircle size={21} weight="fill" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      
        <section className="px-5 pb-20 sm:px-8 sm:pb-28">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">See the workflow</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Review a sales call inside the real Callsect interface.
            </h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">
              Follow the evidence, interpretation, coaching, CRM note, relationship context, and next-call preparation using a synthetic sales conversation.
            </p>
            <div>
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("final", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("final", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
